"use client";

import React, { useEffect, useRef } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import NavBar from "@/components/Header/NavBar";
import { DeregisterRefType, RefsObjectType, RegisterRefType } from "@/components/PageHome/interfaces";
import componentMapping from "@/components/PagePiller/componentMapping";
import { serverReturnType } from "@/components/PagePiller/interfaces";
import { COMMON_COMPONENT_TYPES, NON_ONBOARDED_STATUS, ONBOARDING_STATUS } from "@/helpers/constants";
import routes from "@/helpers/router";
import { pageViewEvent } from "@/helpers/TSDEvents";
import { useAppSelector } from "@/redux/hooks";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import styles from "./PagePillar.module.scss";

interface PillarPageProps {
  pageInfo: serverReturnType;
  pillarSlug: string;
}

const PillarPage: React.FC<PillarPageProps> = ({ pageInfo, pillarSlug }) => {
  const pillarInfo = pageInfo?.pillarData;
  const userAgent = pageInfo?.ua;
  const segmentInformation = pageInfo?.segmentInfoCookie ? JSON.parse(pageInfo?.segmentInfoCookie?.value) : null;

  const signedInStatus = useAppSelector(signedInStateSelector);
  const currentPatientData = useAppSelector(currentPatientSelector);

  const elementRefs = useRef<RefsObjectType>({});

  const registerRef: RegisterRefType = (key, element) => {
    elementRefs.current[key] = element;
  };

  // Function to deregister the ref (cleanup)
  const deregisterRef: DeregisterRefType = (key) => {
    delete elementRefs.current[key]; // Deregister the ref when the child unmounts
  };

  // useSyncPillarData({ pillarInfo, pillarSlug });

  // const pillarData = useAppSelector(PillarSelector);
  const category = pillarInfo?.category;

  const extras = {
    userOnBoarded: pageInfo?.userOnBoarded,
    userAgent,
    categorySlug: pillarInfo?.category?.slug,
    pillarSlug: pillarInfo?.slug,
    registerRef,
    deregisterRef,
    elementRefs,
    productInfo: pageInfo?.productInfo,
    currentSpeciality: pageInfo?.currentSpeciality,
    doctorsArray: pageInfo?.doctorsArray,
    diagnostics: pageInfo?.diagnosticsArray,
  };

  useEffect(() => {
    if (signedInStatus !== SignedinState.UNRESOLVED) {
      const isUserLoggedin = signedInStatus === SignedinState.SIGNEDIN;
      pageViewEvent({
        page: "pillar",
        is_loggedin: isUserLoggedin ? true : false,
        momverse_stage: pageInfo?.userOnBoarded
          ? (ONBOARDING_STATUS[segmentInformation?.type] ?? "")
          : NON_ONBOARDED_STATUS,
        firstname: isUserLoggedin && currentPatientData?.firstName ? currentPatientData?.firstName : "",
        page_name: `Pillar Page: ${pillarInfo?.name}`,
      });
    }
  }, [signedInStatus]);

  // Add ref ID to all content
  const pillarContent = pillarInfo?.content?.map((section, index: number) => ({ ...section, refId: `${index}` }));

  const topPanelSections = pillarContent?.filter((section: any) => section?.Position === "Top");
  const rightPanelSections = pillarContent?.filter(
    (section: any) => section?.Position !== "Left" && section?.Position !== "Top" && section?.Position !== "Bottom"
  );
  const bottomPanelSections = pillarContent?.filter((section: any) => section?.Position === "Bottom");
  const leftPanelSections = pillarContent?.filter((section: any) => section?.Position === "Left");

  const breadCrumbsArray = [
    { label: category?.name, url: routes.category(category?.slug) },
    { label: pillarInfo?.name, url: routes.pillar(pillarInfo?.slug) },
  ];

  const navBarInfo = pillarContent?.find((section) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar);

  extras["indexContent"] = pillarContent?.filter((section: any) => section?.ShowHeadingInIndex);

  if (userAgent?.isMobile) {
    return (
      <>
        <NavBar
          navBarData={navBarInfo}
          userOnBoarded={pageInfo?.userOnBoarded}
          userAgent={userAgent}
          pathname={routes.pillar(pillarSlug)}
        />
        <div className={`container breadcrumb`}>
          <Breadcrumbs options={breadCrumbsArray} />
        </div>
        {pillarContent?.map((data: any, index: number) => {
          return <React.Fragment key={index}>{componentMapping({ data, extras })}</React.Fragment>;
        })}
      </>
    );
  }

  return (
    <>
      {userAgent.isDesktop && (
        <>
          <NavBar
            navBarData={navBarInfo}
            userOnBoarded={pageInfo?.userOnBoarded}
            userAgent={userAgent}
            pathname={routes.pillar(pillarSlug)}
          />
          <div className={`container breadcrumb`}>
            <Breadcrumbs options={breadCrumbsArray} />
          </div>
          {topPanelSections?.map((data: any, index: number) => {
            return <React.Fragment key={`top_${index}`}>{componentMapping({ data, extras, index })}</React.Fragment>;
          })}
          <div className={`${styles.columnsContainer} container `}>
            <div className={styles.leftSection}>
              {leftPanelSections?.map((data: any, index: number) => {
                return (
                  <React.Fragment key={`left_${index}`}>{componentMapping({ data, extras, index })}</React.Fragment>
                );
              })}
            </div>
            <div className={styles.rightSection}>
              {rightPanelSections?.map((data: any, index: number) => {
                return (
                  <React.Fragment key={`right_${index}`}>{componentMapping({ data, extras, index })}</React.Fragment>
                );
              })}
            </div>
          </div>
          <div>
            {bottomPanelSections?.map((data: any, index: number) => {
              return (
                <React.Fragment key={`bottom_${index}`}>{componentMapping({ data, extras, index })}</React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default PillarPage;
